/**
 * Synthetic users used by the Sandbox environment.
 *
 * Sandbox bypasses real auth and simulates a different identity per
 * hub so the demo feels lived-in:
 *   • Admin Hub    → Jorge Martínez  (Smart Canvas, CEO)
 *   • Operator Hub → Aleksei Kavaliou (Smart Canvas, Blockchain Engineer)
 *   • Client Hub   → Tom Becker      (Orbit PM, product owner)
 *
 * The legacy `SANDBOX_USER` / `SANDBOX_PROFILE` exports keep working and
 * point at the admin (Jorge) identity to avoid breakage in older callers.
 */
import type { User } from "@supabase/supabase-js";
import type { AppRole } from "./AuthProvider";

export interface SandboxProfile {
  full_name: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  position: string;
  avatar_url: string | null;
  avatar_color: string;
}

export interface SandboxIdentity {
  id: string;
  roles: AppRole[];
  profile: SandboxProfile;
  user: User;
}

function mkUser(id: string, p: SandboxProfile): User {
  return {
    id,
    aud: "authenticated",
    email: p.email,
    phone: p.phone,
    created_at: new Date(0).toISOString(),
    app_metadata: { provider: "sandbox" },
    user_metadata: {
      full_name: p.full_name,
      company: p.company,
      country: p.country,
      position: p.position,
    },
  } as unknown as User;
}

/* ───────── Admin — Jorge Martínez ───────── */
const adminProfile: SandboxProfile = {
  full_name: "Jorge Martínez",
  company: "Smart Canvas",
  email: "jmartinez@smart-canvas.com",
  phone: "+502 55587541",
  country: "GT",
  position: "CEO & Founder",
  avatar_url: "/team-avatars/tp-100-jorge.png",
  avatar_color: "#0f172a",
};
const adminId = "00000000-0000-0000-0000-00000000sand";

/* ───────── Operator — Aleksei Kavaliou ───────── */
const operatorProfile: SandboxProfile = {
  full_name: "Aleksei Kavaliou",
  company: "Smart Canvas",
  email: "aleksei.kavaliou@smart-canvas.com",
  phone: "+375 29 555 0188",
  country: "BY",
  position: "Lead Smart Contract Engineer",
  avatar_url: "/team-avatars/tp-116-aleksei.webp",
  avatar_color: "#1e293b",
};
const operatorId = "00000000-0000-0000-0000-0000000ops01";

/* ───────── Client — Tom Becker (Orbit PM) ───────── */
const clientProfile: SandboxProfile = {
  full_name: "Tom Becker",
  company: "Orbit PM",
  email: "tom.becker@orbitpm.app",
  phone: "+49 30 5550 0144",
  country: "DE",
  position: "Head of Product",
  avatar_url: null,
  avatar_color: "#312e81",
};
const clientId = "00000000-0000-0000-0000-00000clientx";

export const SANDBOX_IDENTITIES: Record<"admin" | "operator" | "client", SandboxIdentity> = {
  admin: { id: adminId, roles: ["admin"], profile: adminProfile, user: mkUser(adminId, adminProfile) },
  operator: {
    id: operatorId,
    roles: ["operator"],
    profile: operatorProfile,
    user: mkUser(operatorId, operatorProfile),
  },
  client: {
    id: clientId,
    roles: ["client"],
    profile: clientProfile,
    user: mkUser(clientId, clientProfile),
  },
};

/** Derive the sandbox identity from a pathname (defaults to admin). */
export function sandboxIdentityForPath(pathname: string | undefined): SandboxIdentity {
  if (!pathname) return SANDBOX_IDENTITIES.admin;
  if (pathname.startsWith("/os/operator")) return SANDBOX_IDENTITIES.operator;
  if (pathname.startsWith("/os/client")) return SANDBOX_IDENTITIES.client;
  return SANDBOX_IDENTITIES.admin;
}

/* ───────── Backwards-compatible legacy exports (admin persona) ───────── */
export const SANDBOX_USER_ID = adminId;
export const SANDBOX_EMAIL = adminProfile.email;
export const SANDBOX_PROFILE = adminProfile;
export const SANDBOX_ROLES: AppRole[] = SANDBOX_IDENTITIES.admin.roles;
export const SANDBOX_USER: User = SANDBOX_IDENTITIES.admin.user;